/**
 * AssistantThreadActionsContext
 *
 * Lets descendants (e.g. canvas surface components) submit a user message
 * into the host page's assistant-ui runtime without holding a reference to
 * the runtime themselves.
 *
 * The host (OperatorPage) owns the runtime and provides a `submitUserMessage`
 * callback wired to `runtime.thread.append({ role: 'user', ... })`. Appending
 * a user message triggers the adapter the same way a composer-typed message
 * does — so the message renders immediately in the thread and the run
 * streams through the normal adapter path (active runId, surface events,
 * everything).
 */

import { createContext, useContext, type ReactNode } from 'react';

interface AssistantThreadActionsValue {
  /** Append a user message to the thread, which triggers the runtime adapter. */
  submitUserMessage: (text: string) => void;
}

const AssistantThreadActionsContext = createContext<AssistantThreadActionsValue | null>(null);

export function AssistantThreadActionsProvider({
  value,
  children,
}: {
  value: AssistantThreadActionsValue;
  children: ReactNode;
}) {
  return (
    <AssistantThreadActionsContext.Provider value={value}>
      {children}
    </AssistantThreadActionsContext.Provider>
  );
}

/** Returns the actions, or `null` when no provider is mounted. */
export function useAssistantThreadActions(): AssistantThreadActionsValue | null {
  return useContext(AssistantThreadActionsContext);
}
